import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import useClassesStore from '../../../stateManagers/store/useClassesStore';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import apiClient from '../../../stateManagers/services/axiosInterceptor';

// ─── Types ────────────────────────────────────────────────────────────────────

interface StudentData {
  id: number;
  user_id: number;
  name: string;
  class_id: string;
  class_name: string;
  section: string | null;
  gender: string;
  email_id: string;
  admission_no: string;
  roll_no: number;
  dob: string;
  blood_group: string;
  father_name: string;
  mother_name: string;
  father_no: string;
  mother_no: string;
  address: string;
}

interface EditableRow extends StudentData {
  _dirty: boolean;
  _errors: Record<string, string>;
  _submitError: string | null; // error returned from backend per-row
  _submitSuccess: boolean;
  name_edit: string;
  roll_no_edit: string;
  section_edit: string;
  class_id_edit: string;
  father_no_edit: string;
  mother_no_edit: string;
  blood_group_edit: string;
  address_edit: string;
  email_id_edit: string;
  father_name_edit: string;
  mother_name_edit: string;
  gender_edit: string;
}

interface BulkUpdateResult {
  student_id: number;
  status: 'success' | 'error';
  message: string;
}

interface BulkEditStudentsModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  students: StudentData[];
  visibleFields?: string[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'Unknown'];
const GENDERS      = ['Male', 'Female', 'Other'];

const EDITABLE_COLUMNS = [
  { key: 'name_edit',        label: 'Full Name',     type: 'text',     required: true  },
  { key: 'roll_no_edit',     label: 'Roll No',       type: 'number',   required: true  },
  { key: 'section_edit',     label: 'Section',       type: 'section',  required: false },
  { key: 'email_id_edit',    label: 'Email',         type: 'email',    required: true  },
  { key: 'father_name_edit', label: "Father's Name", type: 'text',     required: true  },
  { key: 'mother_name_edit', label: "Mother's Name", type: 'text',     required: false },
  { key: 'father_no_edit',   label: "Father's No",   type: 'tel',      required: true  },
  { key: 'mother_no_edit',   label: "Mother's No",   type: 'tel',      required: false },
  { key: 'blood_group_edit', label: 'Blood Group',   type: 'dropdown', required: false },
  { key: 'gender_edit',      label: 'Gender',        type: 'dropdown', required: true  },
  { key: 'address_edit',     label: 'Address',       type: 'text',     required: false },
] as const;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const resolveClassId = (
  student: StudentData,
  classOptions: { id: string; label: string; sections?: string[] }[],
): string => {
  if (classOptions.some(c => c.id === student.class_id)) return student.class_id;
  const byLabel = classOptions.find(c => c.label === student.class_id);
  if (byLabel) return byLabel.id;
  const byName = classOptions.find(c => c.label === student.class_name);
  if (byName) return byName.id;
  return student.class_id;
};

const toEditableRow = (
  s: StudentData,
  classOptions: { id: string; label: string; sections?: string[] }[],
): EditableRow => {
  const resolvedClassId = resolveClassId(s, classOptions);
  return {
    ...s,
    _dirty:         false,
    _errors:        {},
    _submitError:   null,
    _submitSuccess: false,
    name_edit:        s.name,
    roll_no_edit:     String(s.roll_no),
    section_edit:     s.section ?? '',
    class_id_edit:    resolvedClassId,
    email_id_edit:    s.email_id,
    father_name_edit: s.father_name,
    mother_name_edit: s.mother_name,
    father_no_edit:   s.father_no,
    mother_no_edit:   s.mother_no ?? '',
    blood_group_edit: s.blood_group ?? '',
    address_edit:     s.address ?? '',
    gender_edit:      s.gender ?? '',
  };
};

const detectRollDuplicates = (rows: EditableRow[]): Set<number> => {
  const conflictIds = new Set<number>();
  const dirty = rows.filter(r => r._dirty);
  const seen  = new Map<string, number[]>();

  for (const row of dirty) {
    if (!row.roll_no_edit || Number(row.roll_no_edit) < 1) continue;
    const key = `${row.class_id_edit}__${row.section_edit || 'null'}__${row.roll_no_edit}`;
    const existing = seen.get(key) ?? [];
    existing.push(row.id);
    seen.set(key, existing);
  }

  // Fix 1: Array.from() instead of for...of on Map iterator
  // Fix 2: explicit number type on id
  Array.from(seen.values()).forEach((ids: number[]) => {
    if (ids.length > 1) ids.forEach((id: number) => conflictIds.add(id));
  });

  return conflictIds;
};

const validateRow = (row: EditableRow, rollDuplicates: Set<number>): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!row.name_edit.trim())        errors.name_edit        = 'Required';
  if (!row.roll_no_edit)            errors.roll_no_edit     = 'Required';
  if (Number(row.roll_no_edit) < 1) errors.roll_no_edit     = 'Must be ≥ 1';
  if (rollDuplicates.has(row.id))   errors.roll_no_edit     = 'Duplicate roll no in batch';
  if (!row.email_id_edit.trim())    errors.email_id_edit    = 'Required';
  if (!/\S+@\S+\.\S+/.test(row.email_id_edit)) errors.email_id_edit = 'Invalid email';
  if (!row.father_name_edit.trim()) errors.father_name_edit = 'Required';
  if (!row.father_no_edit.trim())   errors.father_no_edit   = 'Required';
  if (!row.gender_edit)             errors.gender_edit      = 'Required';
  return errors;
};

// ─── Inline cell editors ──────────────────────────────────────────────────────

const CellInput: React.FC<{
  value: string;
  type?: string;
  error?: string;
  submitError?: string | null;
  onChange: (v: string) => void;
  options?: string[];
  disabled?: boolean;
}> = ({ value, type = 'text', error, submitError, onChange, options, disabled }) => {
  const hasError = !!(error || submitError);
  const base: React.CSSProperties = {
    width: '100%',
    padding: '5px 8px',
    borderRadius: '6px',
    border: `1px solid ${hasError ? '#ef4444' : '#e2e8f0'}`,
    fontSize: '12px',
    fontFamily: 'inherit',
    color: '#1e293b',
    background: disabled ? '#f8fafc' : 'white',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color .15s',
    minWidth: '90px',
  };

  if (type === 'dropdown' && options) {
    return (
      <div>
        <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
          style={{ ...base, cursor: disabled ? 'not-allowed' : 'pointer' }}>
          <option value="">—</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        {error && <div style={{ color: '#ef4444', fontSize: '10px', marginTop: 2 }}>{error}</div>}
      </div>
    );
  }

  return (
    <div>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
        style={{ ...base, cursor: disabled ? 'not-allowed' : 'text' }} />
      {error && <div style={{ color: '#ef4444', fontSize: '10px', marginTop: 2 }}>{error}</div>}
    </div>
  );
};

// ─── Per-row submit result banner ─────────────────────────────────────────────

const RowResultBanner: React.FC<{ row: EditableRow }> = ({ row }) => {
  if (!row._submitError && !row._submitSuccess) return null;

  if (row._submitSuccess) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '4px 10px', borderRadius: 8,
        background: '#f0fdf4', border: '1px solid #bbf7d0',
        fontSize: 11, color: '#15803d', fontWeight: 600,
        marginTop: 4,
      }}>
        <i className="pi pi-check-circle" style={{ fontSize: 11 }} />
        Saved successfully
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 8,
      background: '#fef2f2', border: '1px solid #fecaca',
      fontSize: 11, color: '#dc2626', fontWeight: 600,
      marginTop: 4,
    }}>
      <i className="pi pi-times-circle" style={{ fontSize: 11 }} />
      {row._submitError}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const BulkEditStudentsModal: React.FC<BulkEditStudentsModalProps> = ({
  open,
  onClose,
  onSuccess,
  students,
  visibleFields,
}) => {
  const toast           = useRef<Toast>(null);
  const [rows, setRows] = useState<EditableRow[]>([]);
  const [loading, setLoading]       = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isClosing, setIsClosing]   = useState(false);
  const [activeFilter, setActiveFilter] = useState<'all' | 'dirty' | 'error' | 'success'>('all');
  const [globalSearch, setGlobalSearch] = useState('');
  const { classOptions, fetchClasses }  = useClassesStore();

  const activeColumns = visibleFields && visibleFields.length > 0
    ? EDITABLE_COLUMNS.filter(col => visibleFields.includes(col.key))
    : EDITABLE_COLUMNS;

  // ── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      fetchClasses(true);
      setActiveFilter('all');
      setGlobalSearch('');
    }
  }, [open, fetchClasses]);

  useEffect(() => {
    if (open && classOptions.length > 0) {
      setRows(students.map(s => toEditableRow(s, classOptions)));
    }
  }, [open, students, classOptions]);

  // ── Closing animation ─────────────────────────────────────────────────────
  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.bulk-edit-modal .p-dialog') as HTMLElement | null;
      const mask   = document.querySelector('.bulk-edit-modal')           as HTMLElement | null;
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask)   mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => { onClose(); setIsClosing(false); }, 400);
  };

  // ── Duplicate detection (reactive) ────────────────────────────────────────
  const rollDuplicates = detectRollDuplicates(rows);

  // ── Cell update ───────────────────────────────────────────────────────────
  const updateCell = useCallback((rowId: number, field: string, value: string) => {
    setRows(prev => {
      const next = prev.map(r => {
        if (r.id !== rowId) return r;
        return { ...r, [field]: value, _dirty: true, _submitError: null, _submitSuccess: false };
      });
      // Re-run duplicate detection on the updated batch
      const dupes = detectRollDuplicates(next);
      return next.map(r => ({ ...r, _errors: r._dirty ? validateRow(r, dupes) : r._errors }));
    });
  }, []);

  // ── Revert ────────────────────────────────────────────────────────────────
  const revertRow = useCallback((rowId: number) => {
    const original = students.find(s => s.id === rowId);
    if (!original) return;
    setRows(prev => {
      const next = prev.map(r =>
        r.id === rowId ? toEditableRow(original, classOptions) : r,
      );
      const dupes = detectRollDuplicates(next);
      return next.map(r => ({ ...r, _errors: r._dirty ? validateRow(r, dupes) : r._errors }));
    });
  }, [students, classOptions]);

  // ── Derived counts ────────────────────────────────────────────────────────
  const dirtyCount   = rows.filter(r => r._dirty && !r._submitSuccess).length;
  const errorCount   = rows.filter(r => r._dirty && Object.keys(r._errors).length > 0).length;
  const successCount = rows.filter(r => r._submitSuccess).length;
  const backendErrCount = rows.filter(r => !!r._submitError).length;

  // ── Visible rows ──────────────────────────────────────────────────────────
  const visibleRows = rows.filter(r => {
    const matchesFilter =
      activeFilter === 'all'     ? true :
      activeFilter === 'dirty'   ? (r._dirty && !r._submitSuccess) :
      activeFilter === 'error'   ? (r._dirty && (Object.keys(r._errors).length > 0 || !!r._submitError)) :
      r._submitSuccess;

    const q = globalSearch.toLowerCase();
    const matchesSearch = !q ||
      r.name.toLowerCase().includes(q) ||
      r.admission_no.toLowerCase().includes(q) ||
      String(r.roll_no).includes(q);

    return matchesFilter && matchesSearch;
  });

  // ── Section options ───────────────────────────────────────────────────────
  const getSectionsForClass = (classIdEdit: string): string[] => {
    if (!classIdEdit) return [];
    let cls = classOptions.find(c => c.id === classIdEdit);
    if (!cls) cls = classOptions.find(c => c.label === classIdEdit);
    return cls?.sections ?? [];
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const dirtyRows = rows.filter(r => r._dirty && !r._submitSuccess);
    if (dirtyRows.length === 0) {
      toast.current?.show({ severity: 'warn', summary: 'No Changes', detail: 'No rows have been modified.', life: 3000 });
      return;
    }

    // Validate with latest duplicate set
    const dupes = detectRollDuplicates(rows);
    const hasErrors = dirtyRows.some(r => Object.keys(validateRow(r, dupes)).length > 0);
    if (hasErrors) {
      setRows(prev => {
        const d = detectRollDuplicates(prev);
        return prev.map(r => ({ ...r, _errors: r._dirty ? validateRow(r, d) : r._errors }));
      });
      setActiveFilter('error');
      toast.current?.show({
        severity: 'error',
        summary:  'Validation Errors',
        detail:   'Fix the highlighted errors before saving.',
        life:     5000,
      });
      return;
    }

    setSubmitting(true);
    try {
      const payload = dirtyRows.map(r => ({
        student_id:  r.id,
        user_id:     r.user_id,
        name:        r.name_edit.trim(),
        roll_no:     Number(r.roll_no_edit),
        section:     r.section_edit || null,
        class:       r.class_id_edit,
        email_id:    r.email_id_edit.trim(),
        father_name: r.father_name_edit.trim(),
        mother_name: r.mother_name_edit.trim(),
        father_no:   r.father_no_edit.trim(),
        mother_no:   r.mother_no_edit.trim(),
        blood_group: r.blood_group_edit || '',
        address:     r.address_edit.trim(),
        gender:      r.gender_edit,
      }));

      const response = await apiClient.post('/bulk-update-students', { students: payload });
      const data = response.data;

      const resultMap = new Map<number, BulkUpdateResult>(
        (data.results ?? []).map((r: BulkUpdateResult) => [r.student_id, r])
      );

      // Stamp each row with its per-row result
      setRows(prev => prev.map(r => {
        const res = resultMap.get(r.id);
        if (!res) return r;
        if (res.status === 'success') {
          return { ...r, _dirty: false, _submitSuccess: true, _submitError: null, _errors: {} };
        } else {
          return { ...r, _submitError: res.message, _submitSuccess: false };
        }
      }));

      const savedCount  = (data.results ?? []).filter((r: BulkUpdateResult) => r.status === 'success').length;
      const failedCount = (data.results ?? []).filter((r: BulkUpdateResult) => r.status === 'error').length;
      const allSuccess  = failedCount === 0;
      const allFailed   = savedCount  === 0;

      if (allSuccess) {
        toast.current?.show({
          severity: 'success',
          summary:  'All Saved',
          detail:   `${savedCount} student(s) updated successfully`,
          life:     4000,
        });
        onSuccess();
        // Brief delay so user sees the success state before close
        setTimeout(() => handleClose(), 800);
      } else if (allFailed) {
        toast.current?.show({
          severity: 'error',
          summary:  'All Failed',
          detail:   `${failedCount} update(s) failed — see errors below each row`,
          life:     6000,
        });
        setActiveFilter('error');
        // Modal stays open
      } else {
        // Partial — modal stays open, show both counts
        toast.current?.show({
          severity: 'warn',
          summary:  'Partial Success',
          detail:   `${savedCount} saved · ${failedCount} failed — fix errors and retry`,
          life:     6000,
        });
        onSuccess(); // refresh parent list for the ones that did succeed
        setActiveFilter('error');
        // Modal stays open so user can fix and retry failed rows
      }

    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary:  'Network Error',
        detail:   err.response?.data?.message || 'An error occurred during bulk update',
        life:     5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Column templates ──────────────────────────────────────────────────────

  const statusTemplate = (row: EditableRow) => {
    if (row._submitSuccess) {
      return <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981', margin: '0 auto', boxShadow: '0 0 0 3px #d1fae5' }} />;
    }
    if (row._submitError) {
      return <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', margin: '0 auto', boxShadow: '0 0 0 3px #fee2e2' }} />;
    }
    if (!row._dirty) {
      return <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#e2e8f0', margin: '0 auto' }} />;
    }
    const hasErr = Object.keys(row._errors).length > 0;
    return <div style={{ width: 8, height: 8, borderRadius: '50%', background: hasErr ? '#ef4444' : '#10b981', margin: '0 auto' }} />;
  };

  const nameTemplate = (row: EditableRow) => (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{
          width: 30, height: 30, borderRadius: 8,
          background: row._submitSuccess
            ? 'linear-gradient(135deg,#059669,#10b981)'
            : row._submitError
              ? 'linear-gradient(135deg,#dc2626,#ef4444)'
              : 'linear-gradient(135deg,#4169E1,#5B7CE6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'white', fontSize: 12, fontWeight: 700, flexShrink: 0,
        }}>
          {row._submitSuccess
            ? <i className="pi pi-check" style={{ fontSize: 11 }} />
            : row._submitError
              ? <i className="pi pi-times" style={{ fontSize: 11 }} />
              : row.name.charAt(0).toUpperCase()
          }
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.name}</div>
          <div style={{ fontSize: 10, color: '#9ca3af' }}>#{row.admission_no}</div>
        </div>
      </div>
      {/* Per-row backend result banner */}
      <RowResultBanner row={row} />
    </div>
  );

  const cellTemplate = (field: string, type: string, rowData: EditableRow) => {
    // Saved rows — show read-only value
    if (rowData._submitSuccess) {
      const val = (rowData as any)[field];
      return <span style={{ fontSize: 12, color: '#059669', fontWeight: 500 }}>{val ?? '—'}</span>;
    }

    let options: string[] | undefined;
    let resolvedType = type;

    if (resolvedType === 'dropdown') {
      options = field === 'blood_group_edit' ? BLOOD_GROUPS : GENDERS;
    }

    if (resolvedType === 'section') {
      const sections = getSectionsForClass(rowData.class_id_edit);
      if (sections.length === 0) return <span style={{ fontSize: 11, color: '#9ca3af' }}>N/A</span>;
      options      = sections;
      resolvedType = 'dropdown';
    }

    return (
      <CellInput
        value={(rowData as any)[field]}
        type={resolvedType}
        error={(rowData._errors as any)[field]}
        onChange={v => updateCell(rowData.id, field, v)}
        options={options}
        disabled={submitting}
      />
    );
  };

  const actionsTemplate = (row: EditableRow) => (
    <Button
      icon="pi pi-undo"
      className="p-button-text p-button-rounded"
      tooltip="Revert changes"
      tooltipOptions={{ position: 'top' }}
      disabled={(!row._dirty && !row._submitError) || submitting || row._submitSuccess}
      style={{ width: 28, height: 28, color: (row._dirty || row._submitError) && !row._submitSuccess ? '#f59e0b' : '#d1d5db' }}
      onClick={() => revertRow(row.id)}
    />
  );

  // ── Header ────────────────────────────────────────────────────────────────

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '8px 0' }}>
      <div style={{ width: 54, height: 54, borderRadius: 14, background: 'linear-gradient(135deg,#1e3a8a,#3b82f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 22, flexShrink: 0, border: '3px solid white', boxShadow: '0 8px 24px rgba(59,130,246,.3)' }}>
        <i className="pi pi-users" />
      </div>
      <div>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#1e40af', marginBottom: 4 }}>Bulk Edit Students</div>
        <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
          {students.length} students selected
          {activeColumns.length < EDITABLE_COLUMNS.length && (
            <span style={{ marginLeft: 6, padding: '2px 8px', borderRadius: 12, background: '#eff6ff', color: '#3b82f6', fontSize: 12, fontWeight: 600 }}>
              {activeColumns.length} of {EDITABLE_COLUMNS.length} fields
            </span>
          )}
        </div>
      </div>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        style={{ width: '98vw', maxWidth: 1300, borderRadius: 24, overflow: 'hidden' }}
        contentStyle={{
          padding: 18,
          background: 'linear-gradient(180deg,#f8fafc,#f1f5f9)',
          maxHeight: 'calc(92vh - 120px)',
          overflowY: 'auto',
        }}
        headerStyle={{
          background: 'white',
          borderBottom: '1px solid #e5e7eb',
          padding: '20px 24px',
          borderRadius: '24px 24px 0 0',
        }}
        modal
        dismissableMask={!submitting}
        draggable={false}
        resizable={false}
        blockScroll
        maskClassName="bulk-edit-modal"
      >
        <style>{`
          .bulk-edit-modal { backdrop-filter:blur(4px); background-color:rgba(0,0,0,.25)!important; animation:maskFadeIn .35s ease; }
          .bulk-edit-modal.mask-closing { animation:maskFadeOut .4s ease!important; }
          @keyframes maskFadeIn { from{opacity:0} to{opacity:1} }
          @keyframes maskFadeOut { from{opacity:1} to{opacity:0} }
          .bulk-edit-modal .p-dialog { animation:dialogOpen .35s cubic-bezier(.4,0,.2,1); box-shadow:0 25px 50px -12px rgba(0,0,0,.25)!important; }
          .bulk-edit-modal .p-dialog.dialog-closing { animation:dialogClose .4s cubic-bezier(.4,0,.2,1)!important; }
          @keyframes dialogOpen { from{opacity:0;transform:scale(.92) translateY(-20px)} to{opacity:1;transform:scale(1) translateY(0)} }
          @keyframes dialogClose { from{opacity:1;transform:scale(1) translateY(0)} to{opacity:0;transform:scale(.88) translateY(25px)} }
          .bulk-table .p-datatable-thead > tr > th { background:#f9fafb!important; color:#6b7280!important; font-size:11px!important; font-weight:700!important; text-transform:uppercase!important; letter-spacing:.8px!important; padding:10px 8px!important; border-bottom:1px solid #e5e7eb!important; white-space:nowrap; }
          .bulk-table .p-datatable-tbody > tr > td { padding:8px!important; border-bottom:1px solid #f3f4f6!important; vertical-align:middle; }
          .bulk-table .p-datatable-tbody > tr.row-dirty   { background:rgba(59,130,246,.04)!important; }
          .bulk-table .p-datatable-tbody > tr.row-error   { background:rgba(239,68,68,.04)!important; }
          .bulk-table .p-datatable-tbody > tr.row-success { background:rgba(16,185,129,.04)!important; }
          .bulk-table .p-datatable-tbody > tr:hover { background:rgba(65,105,225,.04)!important; }
          .filter-pill { padding:5px 12px; border-radius:20px; font-size:12px; font-weight:600; cursor:pointer; border:1px solid transparent; transition:all .15s; background:white; color:#64748b; border-color:#e2e8f0; }
          .filter-pill.active-all     { background:#eff6ff; color:#3b82f6; border-color:#bfdbfe; }
          .filter-pill.active-dirty   { background:#fefce8; color:#ca8a04; border-color:#fde68a; }
          .filter-pill.active-error   { background:#fef2f2; color:#dc2626; border-color:#fecaca; }
          .filter-pill.active-success { background:#f0fdf4; color:#059669; border-color:#bbf7d0; }
          .bulk-search { display:flex; align-items:center; gap:8px; background:white; border-radius:12px; padding:8px 14px; border:1px solid rgba(59,130,246,.15); box-shadow:0 1px 4px rgba(30,64,175,.06); }
          .bulk-search input { flex:1; border:none; outline:none; font-size:13px; color:#1f2937; background:transparent; }
          .bulk-search input::placeholder { color:#9ca3af; }
        `}</style>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Active field chips */}
          {activeColumns.length < EDITABLE_COLUMNS.length && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '10px 14px', background: '#f0f9ff', borderRadius: 12, border: '1px solid #bae6fd' }}>
              <i className="pi pi-filter" style={{ fontSize: 12, color: '#0284c7' }} />
              <span style={{ fontSize: 12, color: '#0369a1', fontWeight: 600, marginRight: 4 }}>Editing:</span>
              {activeColumns.map(col => (
                <span key={col.key} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, background: '#e0f2fe', color: '#0369a1', fontWeight: 600, border: '1px solid #bae6fd' }}>
                  {col.label}{col.required && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}
                </span>
              ))}
            </div>
          )}

          {/* Partial success banner */}
          {successCount > 0 && backendErrCount > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderRadius: 12, background: '#fffbeb', border: '1px solid #fde68a' }}>
              <i className="pi pi-exclamation-triangle" style={{ color: '#d97706', fontSize: 16 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>Partial update — {backendErrCount} row(s) failed</div>
                <div style={{ fontSize: 12, color: '#b45309', marginTop: 2 }}>Fix the errors shown below each row and click Save again.</div>
              </div>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#059669' }}>{successCount} saved ✓</span>
            </div>
          )}

          {/* All success banner */}
          {successCount > 0 && backendErrCount === 0 && dirtyCount === 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderRadius: 12, background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              <i className="pi pi-check-circle" style={{ color: '#059669', fontSize: 16 }} />
              <div style={{ fontSize: 13, fontWeight: 700, color: '#065f46' }}>All {successCount} student(s) updated successfully!</div>
            </div>
          )}

          {/* Toolbar */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className={`filter-pill ${activeFilter === 'all' ? 'active-all' : ''}`} onClick={() => setActiveFilter('all')}>
                All <span style={{ opacity: .7 }}>{rows.length}</span>
              </button>
              <button className={`filter-pill ${activeFilter === 'dirty' ? 'active-dirty' : ''}`} onClick={() => setActiveFilter('dirty')}>
                <i className="pi pi-pencil" style={{ fontSize: 10, marginRight: 4 }} />
                Modified <span style={{ opacity: .7 }}>{dirtyCount}</span>
              </button>
              {(errorCount > 0 || backendErrCount > 0) && (
                <button className={`filter-pill ${activeFilter === 'error' ? 'active-error' : ''}`} onClick={() => setActiveFilter('error')}>
                  <i className="pi pi-exclamation-circle" style={{ fontSize: 10, marginRight: 4 }} />
                  Errors <span style={{ opacity: .7 }}>{errorCount + backendErrCount}</span>
                </button>
              )}
              {successCount > 0 && (
                <button className={`filter-pill ${activeFilter === 'success' ? 'active-success' : ''}`} onClick={() => setActiveFilter('success')}>
                  <i className="pi pi-check-circle" style={{ fontSize: 10, marginRight: 4 }} />
                  Saved <span style={{ opacity: .7 }}>{successCount}</span>
                </button>
              )}
            </div>

            <div className="bulk-search" style={{ minWidth: 220 }}>
              <i className="pi pi-search" style={{ fontSize: 13, color: '#3b82f6' }} />
              <input type="text" placeholder="Search name, admission no…" value={globalSearch} onChange={e => setGlobalSearch(e.target.value)} />
              {globalSearch && (
                <button onClick={() => setGlobalSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '0 2px' }}>
                  <i className="pi pi-times" style={{ fontSize: 11 }} />
                </button>
              )}
            </div>
          </div>

          {/* Legend */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 11, color: '#94a3b8' }}>
            <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#e2e8f0', marginRight: 4 }} />Unchanged</span>
            <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#10b981', marginRight: 4 }} />Modified (valid)</span>
            <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#ef4444', marginRight: 4 }} />Has errors</span>
            <span style={{ marginLeft: 'auto' }}>{visibleRows.length} of {rows.length} shown</span>
          </div>

          {/* Table */}
          <div className="bulk-table" style={{ background: 'white', borderRadius: 16, overflow: 'hidden', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,.05)' }}>
            <div style={{ overflowX: 'auto' }}>
              <DataTable
                value={visibleRows}
                loading={loading}
                rowClassName={(row: EditableRow) =>
                  row._submitSuccess ? 'row-success' :
                  row._submitError   ? 'row-error'   :
                  row._dirty
                    ? Object.keys(row._errors).length > 0 ? 'row-error' : 'row-dirty'
                    : ''
                }
                emptyMessage={
                  <div style={{ padding: '40px 20px', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
                    <i className="pi pi-filter-slash" style={{ fontSize: 28, marginBottom: 8, display: 'block' }} />
                    No students match the current filter
                  </div>
                }
                scrollable
                scrollHeight="450px"
              >
                <Column header="" body={statusTemplate} style={{ width: 32, textAlign: 'center' }} />
                <Column header="Student" body={nameTemplate} style={{ minWidth: 180 }} />
                <Column
                  header="Class"
                  body={(r: EditableRow) => <span style={{ fontSize: 12, fontWeight: 600, color: '#1e40af' }}>{r.class_name}</span>}
                  style={{ minWidth: 80 }}
                />
                {activeColumns.map(col => (
                  <Column
                    key={col.key}
                    header={<span>{col.label}{col.required && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}</span>}
                    body={(row: EditableRow) => cellTemplate(col.key, col.type, row)}
                    style={{ minWidth: col.type === 'dropdown' ? 100 : 120 }}
                  />
                ))}
                <Column header="" body={actionsTemplate} style={{ width: 44, textAlign: 'center' }} />
              </DataTable>
            </div>
          </div>

          {/* Footer */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 8 }}>
            <div style={{ fontSize: 13, color: '#64748b' }}>
              {dirtyCount > 0
                ? <><span style={{ fontWeight: 700, color: '#3b82f6' }}>{dirtyCount}</span> row(s) pending</>
                : successCount > 0
                  ? <span style={{ color: '#059669', fontWeight: 600 }}>✓ {successCount} saved</span>
                  : 'No changes yet — edit cells above'
              }
              {errorCount > 0 && (
                <span style={{ marginLeft: 8, color: '#ef4444', fontWeight: 600 }}>· {errorCount} validation error(s)</span>
              )}
              {backendErrCount > 0 && (
                <span style={{ marginLeft: 8, color: '#dc2626', fontWeight: 600 }}>· {backendErrCount} server error(s)</span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <Button
                label="Cancel"
                icon="pi pi-times"
                className="p-button-outlined"
                style={{ borderRadius: 10, fontSize: 13 }}
                onClick={handleClose}
                disabled={submitting}
              />
              <GlobalButton
                label={submitting
                  ? `Saving ${dirtyCount}...`
                  : backendErrCount > 0
                    ? `Retry ${backendErrCount} Failed`
                    : `Save ${dirtyCount} Change${dirtyCount !== 1 ? 's' : ''}`
                }
                onClick={handleSubmit}
                variant="primary"
                size="medium"
                disabled={(dirtyCount === 0 && backendErrCount === 0) || submitting || errorCount > 0}
                loading={submitting}
              />
            </div>
          </div>

        </div>
      </Dialog>
    </>
  );
};

export default BulkEditStudentsModal;